<template>
  <div :class="[$style.authContainer, 'type-body-medium']">
    <!-- Step 2: Plan Selection (full-page component) -->
    <div
      v-if="isSignup && currentStep === 2 && seatFullNotice"
      :class="[$style.seatFullNotice, 'type-body-medium']"
    >
      Your team's organization has no free seats right now, so we've set you up
      with your own account instead. Ask your admin to add seats and re-invite
      you, or continue below.
    </div>
    <PlanSelector
      v-if="isSignup && currentStep === 2"
      :email="email"
      :password="password"
      :initialUserCount="planParams.userCount"
      :initialIsAnnual="planParams.isAnnual"
      @back="currentStep = 1"
    />

    <!-- Panel Layout for Login/Signup Step 1 -->
    <template v-else>
      <!-- Form Panel -->
      <div :class="$style.formPanel">
        <div :class="$style.formWrapper">
          <!-- Invalid/expired invite link: don't render the signup form with a
               false "join your team" promise the backend can't keep. -->
          <div
            v-if="isSignup && inviteToken && inviteInvalid"
            :class="$style.authForm"
          >
            <div :class="$style.formHeader">
              <div :class="$style.mascotContainer">
                <LogoComponent />
              </div>
              <h2 :class="[$style.formTitle, 'type-headline-medium']">
                Invitation Link Not Valid
              </h2>
              <p :class="[$style.formSubtitle, 'type-body-medium']">
                {{
                  inviteReason === 'expired'
                    ? 'This invitation has expired. Ask your organization admin to send a new invite.'
                    : 'This invitation link is invalid or has already been used. Ask your organization admin to send a new invite.'
                }}
              </p>
            </div>
            <div :class="$style.switchMode">
              <NuxtLink
                to="/login"
                :class="$style.switchLink"
              >
                Already have an account? Log in
              </NuxtLink>
            </div>
          </div>

          <form
            v-else-if="mfaChallengeToken"
            :class="$style.authForm"
            data-ph-no-capture
            @submit.prevent="handleMFASubmit"
          >
            <div :class="$style.formHeader">
              <div :class="$style.mascotContainer">
                <LogoComponent />
              </div>
              <h2 :class="$style.formTitle">Two-Factor Authentication</h2>
              <p :class="$style.formSubtitle">
                Enter the six-digit code from your authenticator app or one of
                your recovery codes.
              </p>
            </div>
            <div :class="$style.inputGroup">
              <InputCustom
                v-model="mfaCode"
                :class="$style.authInput"
                :disabled="loading"
                placeholder="Authenticator or recovery code"
                name="mfaCode"
                type="text"
                inputmode="text"
                autocomplete="one-time-code"
                :autoFocus="true"
              />
            </div>
            <Button
              :class="$style.submitButton"
              :disabled="loading || !mfaCode.trim()"
              :text="loading ? 'Verifying...' : 'Verify and Sign In'"
              type="submit"
            />
            <button
              type="button"
              :class="$style.forgotLink"
              @click="cancelMFA"
            >
              Back to login
            </button>
            <p :class="$style.mfaRecoveryHelp">
              Lost access to your authenticator and recovery codes? Contact
              <a
                href="mailto:help@getyogen.com?subject=MFA%20account%20recovery"
              >
                help@getyogen.com
              </a>
              for identity verification and account recovery.
            </p>
            <p
              v-if="errors.submit"
              :class="$style.errorMessage"
            >
              {{ errors.submit }}
            </p>
          </form>

          <form
            v-else
            :class="$style.authForm"
            @submit.prevent="handleSubmit"
          >
            <!-- Step indicator for signup (Step 1); invited users skip plan/payment -->
            <div
              v-if="isSignup && !inviteToken"
              :class="$style.stepIndicatorCompact"
            >
              <div :class="[$style.stepNumberText, $style.active]">
                <div :class="[$style.stepDot, $style.active]">
                  <span>1</span>
                </div>
                <span
                  :class="[
                    $style.stepLabel,
                    $style.active,
                    'type-label-small-emphasized',
                  ]"
                  >Account</span
                >
              </div>

              <div :class="[$style.stepNumberText]">
                <div :class="$style.stepDot">
                  <span>2</span>
                </div>
                <span :class="[$style.stepLabel, 'type-label-small-emphasized']"
                  >Plan</span
                >
              </div>

              <div :class="[$style.stepNumberText]">
                <div :class="$style.stepDot">
                  <span>3</span>
                </div>
                <span :class="[$style.stepLabel, 'type-label-small-emphasized']"
                  >Payment</span
                >
              </div>
            </div>

            <!-- Form Header -->
            <div :class="$style.formHeader">
              <div :class="$style.mascotContainer">
                <LogoComponent />
              </div>
              <h2 :class="[$style.formTitle, 'type-headline-medium']">
                {{
                  isSignup
                    ? 'Create Your Account'
                    : isForgotPassword
                      ? 'Reset Password'
                      : 'Welcome Back'
                }}
              </h2>
              <p :class="[$style.formSubtitle, 'type-body-medium']">
                {{
                  isSignup
                    ? ''
                    : isForgotPassword
                      ? 'Enter your email to reset your password'
                      : 'Sign in to continue'
                }}
              </p>
            </div>

            <div
              v-if="isSignup && inviteToken"
              :class="[$style.ssoNotice, 'type-body-medium']"
            >
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                :class="$style.ssoIcon"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span v-if="inviteDetails">
                You've been invited to join
                <strong>{{ inviteDetails.orgName }}</strong> as
                {{ inviteDetails.role === 'Admin' ? 'an Admin' : 'a Member' }}.
                Set a password to accept.
              </span>
              <span v-else
                >You've been invited to join a team. Sign up with the email
                address the invitation was sent to.</span
              >
            </div>

            <div :class="$style.inputGroup">
              <InputCustom
                v-model="email"
                :class="$style.authInput"
                :disabled="loading || !!inviteDetails"
                placeholder="Enter company email"
                name="email"
                type="email"
                autocomplete="on"
                :autoFocus="true"
                :error="errors.email"
                @update:model-value="
                  (emailStr: string) => {
                    validateEmail(emailStr, false)
                    handleEmailInput(emailStr)
                  }
                "
              />
            </div>

            <div
              v-if="ssoEnabled && oidcAvailable && isLogin"
              :class="[$style.ssoNotice, 'type-body-medium']"
            >
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                :class="$style.ssoIcon"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>SSO available for your organization</span>
            </div>

            <div
              v-if="ssoEnabled && enforceSSO && isLogin"
              :class="[$style.ssoEnforced, 'type-body-medium']"
            >
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                :class="$style.ssoIcon"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                >Your organization requires SSO. Please use "Sign in with Okta"
                below.</span
              >
            </div>

            <div
              v-if="!enforceSSO && !isForgotPassword"
              :class="$style.inputGroup"
            >
              <InputCustom
                v-model="password"
                :class="$style.authInput"
                :disabled="loading"
                :placeholder="
                  isSignup
                    ? 'Password (Min 12 characters)'
                    : 'Enter your password'
                "
                name="password"
                type="password"
                :autocomplete="isSignup ? 'new-password' : 'current-password'"
                :error="errors.password"
                @update:model-value="
                  (pw: string) => validatePassword(pw, false)
                "
              />
            </div>

            <!-- Password Requirements for Signup -->
            <div
              v-if="isSignup && !enforceSSO && !isForgotPassword"
              :class="$style.passwordRequirements"
            >
              <p
                :class="[
                  $style.requirementsTitle,
                  'type-label-small-emphasized',
                ]"
              >
                Password requirements
              </p>
              <div :class="$style.requirementsGrid">
                <div
                  :class="[
                    $style.requirement,
                    passwordChecks.length ? $style.met : '',
                    'type-body-small',
                  ]"
                >
                  <span :class="$style.checkMark">{{
                    passwordChecks.length ? '✓' : '●'
                  }}</span>
                  At least 12 characters
                </div>
                <div
                  :class="[
                    $style.requirement,
                    passwordChecks.uppercase ? $style.met : '',
                    'type-body-small',
                  ]"
                >
                  <span :class="$style.checkMark">{{
                    passwordChecks.uppercase ? '✓' : '●'
                  }}</span>
                  At least 1 uppercase letter
                </div>
                <div
                  :class="[
                    $style.requirement,
                    passwordChecks.lowercase ? $style.met : '',
                    'type-body-small',
                  ]"
                >
                  <span :class="$style.checkMark">{{
                    passwordChecks.lowercase ? '✓' : '●'
                  }}</span>
                  At least 1 lowercase letter
                </div>
                <div
                  :class="[
                    $style.requirement,
                    passwordChecks.number ? $style.met : '',
                    'type-body-small',
                  ]"
                >
                  <span :class="$style.checkMark">{{
                    passwordChecks.number ? '✓' : '●'
                  }}</span>
                  At least 1 number
                </div>
                <div
                  :class="[
                    $style.requirement,
                    passwordChecks.special ? $style.met : '',
                    'type-body-small',
                  ]"
                >
                  <span :class="$style.checkMark">{{
                    passwordChecks.special ? '✓' : '●'
                  }}</span>
                  At least 1 special character
                </div>
              </div>
            </div>

            <div
              v-if="isSignup && !enforceSSO && !isForgotPassword"
              :class="$style.inputGroup"
            >
              <InputCustom
                v-model="confirmPassword"
                :class="$style.authInput"
                :disabled="loading"
                placeholder="Confirm Password"
                name="confirmPassword"
                type="password"
                autocomplete="new-password"
                :error="errors.confirmPassword"
                @update:model-value="
                  (cpw: string) => validateConfirmPassword(cpw, false)
                "
              />
            </div>

            <p
              v-if="isSignup && !enforceSSO"
              :class="[$style.legalNotice, 'type-body-small']"
            >
              Review our
              <a
                href="https://getyogen.com/terms-of-service.html"
                target="_blank"
                rel="noopener noreferrer"
                >Terms of Service</a
              >
              and
              <a
                href="https://getyogen.com/privacy-policy.html"
                target="_blank"
                rel="noopener noreferrer"
                >Privacy Policy</a
              >. You will be asked to accept the Terms after verifying your
              email.
            </p>

            <Button
              v-if="!enforceSSO"
              :class="$style.submitButton"
              :disabled="
                loading ||
                (isSignup && !isFormValid) ||
                (turnstileEnabled &&
                  (isSignup || isForgotPassword) &&
                  !turnstileToken)
              "
              :text="
                loading
                  ? 'Loading...'
                  : isSignup
                    ? 'Continue'
                    : submitButtonText
              "
              type="submit"
            />

            <TurnstileWidget
              v-if="(isSignup || isForgotPassword) && !enforceSSO"
              ref="turnstileRef"
              @verified="(t: string) => (turnstileToken = t)"
              @expired="turnstileToken = ''"
              @error="turnstileToken = ''"
            />

            <NuxtLink
              v-if="!isForgotPassword && isLogin"
              :class="[$style.forgotLink, 'type-label-large']"
              to="/forgotPassword"
            >
              Forgot password?
            </NuxtLink>

            <!-- SSO Divider and Button -->
            <div
              v-if="ssoEnabled && isLogin && !isSignup && !enforceSSO"
              :class="[$style.divider, 'type-body-small']"
            >
              <span>or continue with</span>
            </div>

            <SSOLoginButton
              v-if="ssoEnabled && isLogin && !isSignup"
              :orgId="detectedOrgId"
              :email="email"
              :autoDetected="!!detectedOrgId"
              :disabled="!email || !!errors.email"
            />

            <!-- Error Messages -->
            <p
              v-if="errors.submit && !emailVerificationRequired"
              :class="[$style.errorMessage, 'type-body-small']"
            >
              {{ errors.submit }}
            </p>

            <div
              v-if="emailVerificationRequired"
              :class="[$style.verificationNotice, 'type-body-small']"
            >
              <p>Email verification is required.</p>
              <p v-if="successMessage">{{ successMessage }}</p>
              <button
                type="button"
                :class="[
                  $style.resendBtn,
                  { [$style.disabled]: resendingVerification },
                  'type-label-large',
                ]"
                :disabled="resendingVerification"
                @click="!resendingVerification && resendVerificationEmail()"
              >
                {{
                  resendingVerification
                    ? 'Sending...'
                    : 'Resend verification email'
                }}
              </button>
            </div>
            <!-- Switch Auth Mode Link -->
            <div :class="$style.switchMode">
              <NuxtLink
                :to="signupLoginLink"
                :class="[$style.switchLink, 'type-label-large']"
              >
                {{ signupLoginLinkText }}
              </NuxtLink>
            </div>

            <p
              v-if="successMessage && !emailVerificationRequired"
              :class="[$style.successMessage, 'type-body-small']"
            >
              {{ successMessage }}
            </p>
          </form>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import type { ValidationError } from 'yup'
  import { setLocale, string } from 'yup'
  import { storeToRefs } from 'pinia'
  import LogoComponent from 'assets/svg/yogenLogo.svg'
  import PlanSelector from '~/components/PlanSelector.vue'
  import SSOLoginButton from '~/components/auth/SSOLoginButton.vue'
  import oidcApi from '~/apis/oidcApi'
  import signupLoginAPI from '~/apis/signupLoginApi'
  import { isDisposableEmailDomain } from '~/constants/disposableEmailDomains'
  import { useSignupLoginStore } from '~/stores/signupLoginStore'
  import { useUserStore } from '~/stores/userStore'
  import { apiErrorMessage } from '~/utils/apiUtils'

  interface RequestErrorData {
    message?: string
    error?: string
    emailVerificationRequired?: boolean
    oidc_required?: boolean
    data?: RequestErrorData
  }

  interface RequestError extends RequestErrorData {
    statusCode?: number
    status?: number
    response?: { status?: number }
  }

  const asRequestError = (error: unknown) => error as RequestError
  const requestErrorData = (error: unknown): RequestErrorData => {
    const err = asRequestError(error)
    return err.data?.data ?? err.data ?? {}
  }

  const defaultErrorObject: { [key: string]: string } = {
    submit: '',
    password: '',
    confirmPassword: '',
    email: '',
  }
  const route = useRoute()
  const loading = ref(false)
  const errors = ref({ ...defaultErrorObject })
  const successMessage = ref('')
  const currentStep = ref(1)
  const confirmPassword = ref('')
  const emailVerificationRequired = ref(false)
  const resendingVerification = ref(false)
  const turnstileToken = ref('')
  const turnstileRef = ref<{ reset: () => void } | null>(null)
  const runtimeConfig = useRuntimeConfig()
  // Local environments may omit both Turnstile keys. Deployed environments
  // must configure this public key whenever the backend enforces verification.
  const turnstileEnabled = computed(
    () => !!runtimeConfig.public.turnstileSiteKey,
  )
  const ssoEnabled = computed(() => runtimeConfig.public.ssoEnabled === true)

  const detectedOrgId = ref<string | undefined>(undefined)
  const oidcAvailable = ref(false)
  const enforceSSO = ref(false)

  let emailDebounce: NodeJS.Timeout | null = null
  let lastCheckedEmail = ''

  const handleEmailInput = async (emailValue: string) => {
    if (emailValue !== lastCheckedEmail) {
      oidcAvailable.value = false
      enforceSSO.value = false
      detectedOrgId.value = undefined
    }

    if (emailDebounce) {
      clearTimeout(emailDebounce)
    }

    if (
      !ssoEnabled.value ||
      !emailValue.includes('@') ||
      emailValue.length < 5 ||
      !isLogin
    ) {
      return
    }

    emailDebounce = setTimeout(async () => {
      try {
        const result = await oidcApi.checkByEmail(emailValue)
        lastCheckedEmail = emailValue
        if (result.oidcEnabled) {
          detectedOrgId.value = result.orgId
          oidcAvailable.value = true
          enforceSSO.value = result.enforceSSO
        }
      } catch (err) {
        console.debug('[AuthForm] OIDC check failed:', err)
      }
    }, 500)
  }

  const store = useSignupLoginStore()
  const { email } = storeToRefs(store)
  const password = ref('')
  const mfaChallengeToken = ref('')
  const mfaCode = ref('')

  const isSignup = computed(() => route.path === '/signup')

  // Present when the user arrived via an org-invite email link
  // (/signup?invite=<token>). Invited users skip plan selection entirely.
  const inviteToken = computed(() => (route.query.invite as string) || '')

  // Resolved invite details (org name, invited email) for a valid token; the
  // email field is locked to the invited address while these are set.
  const inviteDetails = ref<{
    orgName?: string
    email?: string
    role?: string
  } | null>(null)
  const inviteInvalid = ref(false)
  const inviteReason = ref('')
  // Set when the backend could not honor the invite because the inviter's org
  // had no free seats — the user got their own trial org instead and needs to
  // know why they are seeing plan selection.
  const seatFullNotice = ref(false)

  onMounted(async () => {
    if (isSignup.value && route.query.step === 'plan') {
      const { isAuthenticated, requiresTermsAcceptance, requiresOnboarding } =
        useAuth()
      if (!isAuthenticated.value) {
        await navigateTo('/login')
        return
      }
      if (requiresTermsAcceptance.value) {
        await navigateTo('/accept-terms')
        return
      }
      if (!requiresOnboarding.value) {
        await navigateTo('/home')
        return
      }

      try {
        const userStore = useUserStore()
        await userStore.getUser()
        email.value = userStore.user.email
        seatFullNotice.value =
          sessionStorage.getItem('yogen_signup_seat_full') === 'true'
        sessionStorage.removeItem('yogen_signup_seat_full')
        currentStep.value = 2
      } catch (err) {
        console.error('[AuthForm] Could not resume plan selection:', err)
        errors.value.submit =
          'We could not resume plan selection. Please refresh and try again.'
      }
      return
    }

    if (!isSignup.value || !inviteToken.value) return
    try {
      const details = await signupLoginAPI.getInviteDetails(inviteToken.value)
      if (details.valid) {
        inviteDetails.value = details
        if (details.email) {
          email.value = details.email
          await validateEmail(details.email, false)
        }
      } else {
        inviteInvalid.value = true
        inviteReason.value = details.reason || ''
      }
    } catch (err) {
      // Can't verify the token (network/server error): fall back to the plain
      // invite flow rather than falsely declaring the invite invalid.
      console.error('[AuthForm] Invite lookup failed:', err)
    }
  })

  const planParams = computed(() => ({
    userCount: route.query.userCount
      ? parseInt(route.query.userCount as string)
      : 1,
    isAnnual: route.query.isAnnual === 'true',
    plan: (route.query.plan as string) || '',
  }))

  const passwordChecks = computed(() => ({
    length: password.value.length >= 12,
    uppercase: /[A-Z]/.test(password.value),
    lowercase: /[a-z]/.test(password.value),
    number: /[0-9]/.test(password.value),
    special: /[-!@#$%^&*(),.?":{}|<>_[\]/+=\\]/.test(password.value),
  }))

  const allPasswordRequirementsMet = computed(() => {
    return (
      passwordChecks.value.length &&
      passwordChecks.value.uppercase &&
      passwordChecks.value.lowercase &&
      passwordChecks.value.number &&
      passwordChecks.value.special
    )
  })

  const isFormValid = computed(() => {
    if (!isSignup.value) return true

    return (
      email.value &&
      !errors.value.email &&
      password.value &&
      allPasswordRequirementsMet.value &&
      !errors.value.password &&
      confirmPassword.value &&
      !errors.value.confirmPassword &&
      password.value === confirmPassword.value
    )
  })

  const handleWrapper =
    (
      storeFunction: () => Promise<{ message?: string } | undefined>,
      validationFunction: () => Promise<boolean | undefined>,
      redirect?: string,
      successMsg?: string,
    ) =>
    async () => {
      if (loading.value) return
      successMessage.value = ''
      loading.value = true
      errors.value.submit = ''

      try {
        const isValid = await validationFunction()

        if (isValid === false) {
          loading.value = false
          return
        }

        const response = await storeFunction()

        if (isSignup.value) {
          currentStep.value = 2
          loading.value = false
          return
        }

        if (successMsg || response?.message) {
          successMessage.value = successMsg ?? response?.message ?? ''
          setTimeout(() => {
            if (redirect) navigateTo(redirect)
          }, 1000)
        } else if (redirect) navigateTo(redirect)
      } catch (error) {
        const err = asRequestError(error)
        console.error('Error processing request:', err)
        if (err?.message && !err.message.includes('validation')) {
          if (route.path.includes('forgot')) {
            errors.value.submit = apiErrorMessage(
              err,
              'We could not send the password reset email. Please try again.',
            )
          } else {
            errors.value.submit = 'An error occurred. Please try again.'
          }
        }
      } finally {
        setTimeout(() => {
          loading.value = false
        }, 2000)
      }
    }

  const validateEmail = async (em: string, validateEmpty: boolean = true) => {
    errors.value.email = ''
    try {
      if (!em && !validateEmpty) return

      // Invited users sign up with whatever address the admin invited —
      // including personal domains (gmail etc.), which the backend accepts.
      // Only organic signups are held to the company-email rule; enforcing it
      // on a locked, prefilled invited address made the form unsubmittable.
      const schema = inviteToken.value ? invitedEmailSchema : emailSchema
      await schema.validate(em, { abortEarly: false })

      if (isDisposableEmailDomain(em)) {
        errors.value.email =
          'This email domain is not accepted at Yogen at this time.'
        return
      }
    } catch (e) {
      const vError = e as ValidationError
      vError.inner?.forEach((validationError) => {
        errors.value['email'] = validationError.message
      })
    }
  }

  const validatePassword = async (
    pw: string,
    validateEmpty: boolean = true,
  ) => {
    errors.value.password = ''

    try {
      if (!pw && !validateEmpty) return

      await passwordSchema.validate(pw, { abortEarly: false })
    } catch (e) {
      const vError = e as ValidationError
      vError.inner?.forEach((validationError) => {
        errors.value['password'] = validationError.message
      })
    }
  }

  const validateConfirmPassword = (
    confirmPw: string,
    validateEmpty: boolean = true,
  ) => {
    errors.value.confirmPassword = ''

    if (!confirmPw && !validateEmpty) return

    if (password.value !== confirmPw) {
      errors.value.confirmPassword = 'ⓘ Passwords do not match'
    }
  }

  const validateForm = async () => {
    const validations = [
      validateEmail(email.value),
      validatePassword(password.value),
    ]

    if (isSignup.value) {
      validateConfirmPassword(confirmPassword.value)
    }

    await Promise.all(validations)

    if (
      !!errors.value.email ||
      !!errors.value.password ||
      !!errors.value.confirmPassword
    ) {
      return false
    }
    return true
  }

  const handleLogin = async () => {
    if (loading.value) return
    successMessage.value = ''
    loading.value = true
    errors.value.submit = ''
    emailVerificationRequired.value = false

    try {
      const isValid = await validateForm()
      if (isValid === false) {
        loading.value = false
        return
      }

      const loginResponse = await store.login(password.value)

      const auth = loginResponse?.data?.value
      if (auth?.mfaRequired && auth?.mfaChallengeToken) {
        mfaChallengeToken.value = auth.mfaChallengeToken
        mfaCode.value = ''
        loading.value = false
        return
      }

      // Track successful login in PostHog
      const { $posthog } = useNuxtApp()
      $posthog?.()?.capture('login_success')

      if (auth?.termsAcceptanceRequired) {
        sessionStorage.setItem(
          'yogen_pending_terms_version',
          auth.termsVersion || '',
        )
        await navigateTo('/accept-terms')
      } else if (auth?.onboardingRequired) {
        await navigateTo('/signup?step=plan')
      } else {
        await navigateTo('/home')
      }
    } catch (error) {
      const err = asRequestError(error)
      console.error('Error during login:', err)
      // The Nuxt server route forwards the backend's JSON body via createError({ data }).
      // Depending on the $fetch version the payload lands on err.data.data or err.data.
      const backendData = requestErrorData(error)
      const statusCode = err?.statusCode ?? err?.status ?? err?.response?.status
      if (backendData.emailVerificationRequired) {
        emailVerificationRequired.value = true
      } else if (backendData.oidc_required) {
        errors.value.submit =
          'Your organization requires SSO. Please sign in with SSO below.'
      } else if (statusCode === 401) {
        errors.value.submit =
          'Login failed. Please check your credentials and try again.'
      } else if (statusCode === 503) {
        errors.value.submit =
          backendData.message ||
          'Authentication is temporarily unavailable. Please try again shortly.'
      } else {
        errors.value.submit = 'Login failed. Please try again.'
      }
    } finally {
      setTimeout(() => {
        loading.value = false
      }, 2000)
    }
  }

  const handleMFASubmit = async () => {
    if (loading.value) return
    if (!mfaChallengeToken.value || !mfaCode.value.trim()) return
    loading.value = true
    errors.value.submit = ''
    try {
      const response = await store.verifyMFA(
        mfaChallengeToken.value,
        mfaCode.value.trim(),
      )
      const auth = response?.data?.value
      const { $posthog } = useNuxtApp()
      $posthog?.()?.capture('login_success', { mfa: true })
      mfaChallengeToken.value = ''
      mfaCode.value = ''
      if (auth?.termsAcceptanceRequired) {
        sessionStorage.setItem(
          'yogen_pending_terms_version',
          auth.termsVersion || '',
        )
        await navigateTo('/accept-terms')
      } else if (auth?.onboardingRequired) {
        await navigateTo('/signup?step=plan')
      } else {
        await navigateTo('/home')
      }
    } catch (error) {
      const backendData = requestErrorData(error)
      errors.value.submit =
        backendData.message ||
        backendData.error ||
        'That code is invalid or expired. Try again or return to login.'
    } finally {
      loading.value = false
    }
  }

  const cancelMFA = () => {
    mfaChallengeToken.value = ''
    mfaCode.value = ''
    password.value = ''
    errors.value.submit = ''
  }

  const resendVerificationEmail = async () => {
    resendingVerification.value = true
    successMessage.value = ''
    errors.value.submit = ''

    try {
      if (!email.value) {
        errors.value.submit = 'Please enter your email address'
        return
      }

      const response = await signupLoginAPI.resendVerification(email.value)
      successMessage.value = response.message
      // Keep the verification notice visible until the user follows the link.
      // Hiding it after "resend" made the account look ready even though it
      // was still blocked by the backend.
      emailVerificationRequired.value = true
    } catch (error) {
      console.error('Error resending verification email:', error)
      errors.value.submit =
        'Failed to resend verification email. Please try again.'
    } finally {
      resendingVerification.value = false
    }
  }

  const handleSignupStep1 = async () => {
    if (loading.value) return
    loading.value = true
    errors.value.submit = ''

    try {
      const isValid = await validateForm()

      if (isValid === false) {
        loading.value = false
        return
      }

      if (turnstileEnabled.value && !turnstileToken.value) {
        errors.value.submit = 'Please complete the verification challenge.'
        loading.value = false
        return
      }

      // Create the account NOW, while the Turnstile token is fresh (tokens are
      // single-use and expire in ~5 minutes). Plan selection / payment happen
      // afterwards against an existing account, so a slow checkout can never
      // leave a paying customer without an account.
      const response = await store.signup(
        password.value,
        turnstileToken.value,
        inviteToken.value || undefined,
      )

      // Mark the top of the funnel without identifying the person. Every self-serve
      // signup lands here (before plan selection), so this is the authoritative
      // "account_created" moment — invited users included.
      const { $posthog } = useNuxtApp()
      const posthog = $posthog?.()
      if (posthog && response?.user?.id) {
        posthog.capture('account_created', {
          invited: !!response?.joinedOrg,
        })
      }

      // Verification is a real gate on checkout and other protected actions.
      // Do not advance into either the app or plan selection before it is
      // complete.
      if (response?.emailVerificationRequired) {
        emailVerificationRequired.value = true
        successMessage.value =
          response.message ||
          'Account created. Check your email before continuing.'
        loading.value = false
        return
      }

      if (response?.joinedOrg) {
        // Backend joined the inviter's org (by token, or by email match) — no
        // plan selection or payment step for invited users.
        sessionStorage.setItem(
          'yogen_signup_data',
          JSON.stringify({
            email: email.value,
            invited: true,
            orgName: response.joinedOrgName || '',
            timestamp: Date.now(),
          }),
        )
      }

      if (response?.inviteNotJoinedReason === 'seat_limit') {
        sessionStorage.setItem('yogen_signup_seat_full', 'true')
      }

      // Every new account accepts the current Terms before entering either the
      // product (invitees) or plan selection (organic owners). The durable
      // onboarding marker decides which destination follows acceptance.
      await navigateTo('/accept-terms')
    } catch (error) {
      const err = asRequestError(error)
      console.error('Error during signup:', err)
      const message: string = err?.message || ''
      if (message.includes('already exists')) {
        errors.value.submit =
          'An account with this email already exists. Please log in instead.'
      } else if (message.includes('captcha_failed')) {
        errors.value.submit = turnstileEnabled.value
          ? 'Verification failed. Please complete the challenge again.'
          : 'Verification is unavailable. Please refresh the page and try again. If no verification challenge appears, contact support.'
      } else if (message.includes('password must')) {
        errors.value.submit = message
      } else if (!message.includes('validation')) {
        errors.value.submit = 'An error occurred. Please try again.'
      }
      // The backend consumes the Turnstile token on every attempt — always
      // reset the widget so a retry gets a fresh token.
      turnstileRef.value?.reset()
      turnstileToken.value = ''
      loading.value = false
    }
  }

  const handleForgotPassword = handleWrapper(
    async () => {
      if (turnstileEnabled.value && !turnstileToken.value) {
        throw new Error('Please complete the verification challenge.')
      }
      try {
        return await store.forgotPassword(turnstileToken.value)
      } finally {
        turnstileRef.value?.reset()
        turnstileToken.value = ''
      }
    },
    async () => {
      await validateEmail(email.value)
      return !errors.value.email
    },
  )

  const authConfig: {
    [key: string]: {
      handleSubmit: () => void
      submitButtonText: string
      signupLoginLink: string
      signupLoginLinkText: string
    }
  } = {
    ['/login']: {
      handleSubmit: handleLogin,
      submitButtonText: 'Sign In',
      signupLoginLink: '/signup',
      signupLoginLinkText: 'Sign up',
    },
    ['/signup']: {
      handleSubmit: handleSignupStep1,
      submitButtonText: 'Sign up',
      signupLoginLink: '/login',
      signupLoginLinkText: 'Go to log in',
    },
    ['/forgotPassword']: {
      handleSubmit: handleForgotPassword,
      submitButtonText: 'Reset Password',
      signupLoginLink: '/login',
      signupLoginLinkText: 'Back to login',
    },
  }

  const computedValues = computed(() => {
    const isForgotPassword = route.path === '/forgotPassword'
    const isLogin = route.path === '/login'
    const config = authConfig[route.path]
    return {
      ...config,
      isForgotPassword,
      isLogin,
    }
  })

  const {
    submitButtonText,
    handleSubmit,
    signupLoginLink,
    signupLoginLinkText,
    isForgotPassword,
    isLogin,
  } = computedValues.value

  const PASSWORD_CHARACTER_MINIMUM = isSignup.value ? 12 : 1
  setLocale({
    string: {
      email: 'Invalid email',
      min: `ⓘ Password must be at least ${PASSWORD_CHARACTER_MINIMUM} characters`,
    },
  })

  const personalEmailDomains = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'aol.com',
    'icloud.com',
    'protonmail.com',
    'zoho.com',
    'yandex.com',
    'mail.com',
    'gmx.com',
    'tutanota.com',
    'dispostable.com',
    '10minutemail.com',
    'guerrillamail.com',
    'mailinator.com',
    'temp-mail.org',
    'throwaway.email',
    'fakeinbox.com',
    'maildrop.cc',
    'tempail.com',
    'yopmail.com',
    'mailcatch.com',
    'spamgourmet.com',
    'trashmail.com',
    'getnada.com',
    'mail-temporaire.fr',
    'temp-mail.ru',
    '0-mail.com',
    'anonbox.net',
    'mailnull.com',
    'incognitomail.org',
    'mailmetrash.com',
    'spam4.me',
    'binkmail.com',
    'dodgit.com',
    'e4ward.com',
    'gishpuppy.com',
    'guerrillamail.biz',
    'guerrillamail.com',
    'guerrillamail.de',
    'guerrillamail.net',
    'guerrillamail.org',
    'guerrillamailblock.com',
    'pokemail.net',
    'spamgourmet.com',
    'spamgourmet.net',
    'spamgourmet.org',
    'suremail.info',
    'teleworm.com',
    'thisisnotmyrealemail.com',
    'upliftnow.com',
    'uplipht.com',
    'venompen.com',
    'veryrealemail.com',
    'walkmail.net',
    'wetrainbayarea.com',
    'wetrainbayarea.org',
    'zetmail.com',
  ]

  // Base format rules shared by organic and invited signups.
  const invitedEmailSchema = string()
    .email()
    .required()
    .matches(
      /^[a-zA-Z0-9._%+-]+@(?!.*\.\.)(?!.*\.([a-zA-Z]{2,})\.\1$)[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      'ⓘ Please enter a valid email address.',
    )

  // Organic signups additionally require a company (non-personal) domain.
  const emailSchema = invitedEmailSchema.test(
    'company-email',
    'ⓘ The email used must be a company email where you receive emails.',
    (value) => {
      if (!value) return true
      const parts = value.split('@')
      if (parts.length !== 2) return false
      const domain = parts[1].toLowerCase()
      return !personalEmailDomains.includes(domain)
    },
  )

  const passwordSchema = isSignup.value
    ? string()
        .min(
          PASSWORD_CHARACTER_MINIMUM,
          `ⓘ Password must be at least ${PASSWORD_CHARACTER_MINIMUM} characters`,
        )
        .matches(
          /[A-Z]/,
          'ⓘ Password must contain at least one uppercase letter',
        )
        .matches(
          /[a-z]/,
          'ⓘ Password must contain at least one lowercase letter',
        )
        .matches(/[0-9]/, 'ⓘ Password must contain at least one number')
        .matches(
          /[-!@#$%^&*(),.?":{}|<>_[\]/+=\\]/,
          'ⓘ Password must contain at least one special character',
        )
        .required()
    : string().min(1).required()
</script>

<style module>
  .seatFullNotice {
    position: sticky;
    top: 0;
    z-index: 20;
    background: var(--color-status-warning-subtle);
    color: var(--color-text-primary);
    padding: var(--space-12) var(--space-20);
    text-align: center;
  }

  /* ===== Main Container ===== */
  .authContainer {
    display: flex;
    min-height: 100vh;
    width: 100%;
  }

  /* ===== Form Panel ===== */
  .formPanel {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 100vh;
    background: var(--gradient-background-page);
    padding: var(--space-32);
  }

  .formWrapper {
    width: 100%;
    max-width: 600px;
  }

  .formHeader {
    margin-bottom: var(--space-32);
    text-align: center;
  }

  .mascotContainer {
    filter: drop-shadow(var(--effect-brand-glow));
  }

  .formTitle {
    color: var(--color-text-primary);
    margin: 0 0 var(--space-8) 0;
  }

  .formSubtitle {
    color: var(--color-text-secondary);
    margin: 0;
  }

  /* ===== Step Indicator (Compact) ===== */
  .stepIndicatorCompact {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-8);
    margin-bottom: var(--space-24);
  }

  .stepDot {
    width: 48px;
    height: 48px;
    border-radius: var(--radius-full);
    background: var(--color-border-default);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
  }

  .stepDot span {
    color: var(--color-text-disabled);
  }

  .stepDot.active {
    background: var(--gradient-brand-medium);
  }

  .stepDot.active span {
    color: var(--color-text-on-brand);
  }

  .stepLabel {
    color: var(--color-text-secondary);
    white-space: nowrap;
  }

  .stepLabel.active {
    color: var(--color-text-primary);
  }

  .stepNumberText {
    height: 100%;
    width: 122px;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: var(--space-8);
    display: inline-flex;
    text-align: center;
    color: var(--color-text-secondary);
  }

  .stepNumberText.active {
    color: var(--color-text-primary);
  }

  /* ===== Form Styles ===== */
  .authForm {
    display: flex;
    flex-direction: column;
    gap: var(--space-20);
    background: var(--color-surface-default);
    padding: var(--space-40) var(--space-20) var(--space-40) var(--space-20);
  }

  .inputGroup {
    display: flex;
    flex-direction: column;
    gap: var(--space-8);
  }

  .authInput {
    width: 100% !important;
    max-width: 100% !important;
  }

  .authInput :global(div) {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* ===== SSO Notices ===== */
  .ssoNotice {
    display: flex;
    align-items: center;
    gap: var(--space-8);
    padding: var(--space-12) var(--space-16);
    background: var(--color-surface-accent);
    color: var(--color-interactive-link-hover);
    border-radius: var(--radius-sm);
  }

  .ssoEnforced {
    display: flex;
    align-items: flex-start;
    gap: var(--space-8);
    padding: var(--space-16);
    background: var(--color-status-warning-subtle);
    color: var(--color-text-primary);
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-status-warning);
  }

  .ssoIcon {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  /* ===== Password Requirements ===== */
  .passwordRequirements {
    background: var(--color-surface-muted);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    padding: var(--space-16);
  }

  .requirementsTitle {
    color: var(--color-text-secondary);
    margin: 0 0 var(--space-12) 0;
    text-transform: uppercase;
  }

  .requirementsGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-8);
  }

  .requirement {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    color: var(--color-text-secondary);
    transition: color 0.2s ease;
  }

  .requirement.met {
    color: var(--color-status-success-text);
  }

  .checkMark {
    width: 1rem;
    text-align: center;
  }

  /* ===== Buttons & Links ===== */
  .legalNotice {
    margin: 0;
    color: var(--color-text-secondary);
    text-align: center;
  }

  .legalNotice a {
    color: var(--color-interactive-primary);
    text-decoration: underline;
  }

  .submitButton {
    width: 100%;
    margin-top: var(--space-8);
  }

  .forgotLink {
    composes: text-link from global;
    display: block;
    text-align: center;
    color: var(--color-interactive-primary);
    text-decoration: none;
  }

  .forgotLink:hover {
    color: var(--color-interactive-primary-hover);
    text-decoration: underline;
  }

  .mfaRecoveryHelp {
    margin: 0;
    color: #667085;
    font-size: 0.875rem;
    line-height: 1.5;
    text-align: center;

    a {
      color: var(--color-purple-accent);
      font-weight: 600;
    }
  }

  .divider {
    display: flex;
    align-items: center;
    text-align: center;
    margin: var(--space-8) 0;
    color: var(--color-text-disabled);
  }

  .divider::before,
  .divider::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--color-border-default);
  }

  .divider span {
    padding: 0 var(--space-16);
  }

  .switchMode {
    text-align: center;
  }

  .switchLink {
    composes: text-link from global;
    color: var(--color-interactive-link);
    text-decoration: none;
    margin-left: var(--space-4);
  }

  .switchLink:hover {
    color: var(--color-text-primary);
  }

  /* ===== Messages ===== */
  .errorMessage {
    color: var(--color-status-error);
    text-align: center;
    margin: 0;
    padding: var(--space-12);
    background: var(--color-status-error-subtle);
    border-radius: var(--radius-sm);
  }

  .successMessage {
    color: var(--color-status-success);
    text-align: center;
    margin: 0;
    padding: var(--space-12);
    background: var(--color-status-success-subtle);
    border-radius: var(--radius-sm);
  }

  .verificationNotice {
    text-align: center;
    padding: var(--space-16);
    background: var(--color-status-warning-subtle);
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-status-warning);
  }

  .verificationNotice p {
    color: var(--color-text-primary);
    margin: 0 0 var(--space-12) 0;
  }

  .resendBtn {
    background: transparent;
    border: none;
    color: var(--color-interactive-primary);
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
  }

  .resendBtn:hover:not(.disabled) {
    color: var(--color-interactive-primary-hover);
  }

  .resendBtn.disabled {
    color: var(--color-interactive-link-disabled);
    cursor: wait;
  }

  /* ===== Responsive Design ===== */
  @media (max-width: 768px) {
    .authContainer {
      flex-direction: column;
    }

    .formPanel {
      width: 100%;
      min-height: auto;
      padding: var(--space-32) var(--space-24) var(--space-48);
    }

    .formWrapper {
      max-width: 100%;
    }
  }

  @media (max-width: 480px) {
    .formPanel {
      padding: var(--space-24) var(--space-16) var(--space-32);
    }

    .formTitle {
    }

    .requirementsGrid {
      grid-template-columns: 1fr;
    }

    .stepDot {
      width: 28px;
      height: 28px;
    }
  }
</style>
